import React, { useState, useEffect } from 'react';
import { locales } from './locales';
import { gameData } from './data';
import { useTimer } from './hooks/useTimer';
import { generateRoles } from './utils/gameUtils';
import HowToPlay from './components/HowToPlay';

// Components
import Header from './components/Header';
import GameSetup from './components/GameSetup';
import RoleAssignment from './components/RoleAssignment';
import GameTimer from './components/GameTimer';
import GameInfo from './components/GameInfo';

function App() {
  // Game state
  const [gameState, setGameState] = useState('setup');
  const [gameMode, setGameMode] = useState('location');
  const [numPlayers, setNumPlayers] = useState(4);
  const [numSpies, setNumSpies] = useState(1);
  const [gameTimer, setGameTimer] = useState(5);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('spyGameTheme') !== 'light';
  });
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Role assignment state
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [roles, setRoles] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [roleRevealed, setRoleRevealed] = useState(false);
  const [selectedHumanCategory, setSelectedHumanCategory] = useState('');

  // Language state
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('spyGameLanguage') || 'en';
  });

  // Timer hook
  const { timeLeft, startTimer, stopTimer, formatTime } = useTimer();

  const t = locales[language];

  // Effects
  useEffect(() => {
    localStorage.setItem('spyGameLanguage', language);
  }, [language]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }
    localStorage.setItem('spyGameTheme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLanguageDropdown && !event.target.closest('.language-dropdown')) {
        setShowLanguageDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageDropdown]);

  // Game functions
  const startGame = () => {
    const { roles: newRoles, selectedItem: newSelectedItem } = generateRoles(
      numPlayers,
      numSpies,
      gameMode,
      gameData,
      language,
      selectedHumanCategory
    );

    setRoles(newRoles);
    setSelectedItem(newSelectedItem);
    setGameState('roleAssignment');
    setCurrentPlayer(0);
    setRoleRevealed(false);
  };

  const nextPlayer = () => {
    setRoleRevealed(false);
    if (currentPlayer < numPlayers - 1) {
      setCurrentPlayer(currentPlayer + 1);
    } else {
      startTimer(gameTimer);
      setGameState('playing');
    }
  };

  const newGame = () => {
    setGameState('setup');
    setCurrentPlayer(0);
    setRoles([]);
    setSelectedItem('');
    setRoleRevealed(false);
    stopTimer();
  };
  
  const anotherRound = () => {
    const { roles: newRoles, selectedItem: newSelectedItem } = generateRoles(
      numPlayers,
      numSpies,
      gameMode,
      gameData,
      language
    );

    setRoles(newRoles);
    setSelectedItem(newSelectedItem);
    setGameState('roleAssignment');
    setCurrentPlayer(0);
    setRoleRevealed(false);
    stopTimer();
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <div className={`min-h-screen bg-spy-bg font-ui text-spy-text transition-colors duration-200 ${darkMode ? 'dark' : ''}`}>
      <div className="mx-auto flex min-h-screen max-w-[560px] flex-col px-[22px] py-5">
        <Header
          title={t.title}
          subtitle={t.subtitle}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          language={language}
          setLanguage={setLanguage}
          showLanguageDropdown={showLanguageDropdown}
          setShowLanguageDropdown={setShowLanguageDropdown}
          onShowHowToPlay={() => setShowHowToPlay(true)}
        />

        {gameState === 'setup' && (
          <GameSetup
            gameMode={gameMode}
            setGameMode={setGameMode}
            numPlayers={numPlayers}
            setNumPlayers={setNumPlayers}
            numSpies={numSpies}
            setNumSpies={setNumSpies}
            gameTimer={gameTimer}
            setGameTimer={setGameTimer}
            onStartGame={startGame}
            t={t}
            selectedHumanCategory={selectedHumanCategory}
            setSelectedHumanCategory={setSelectedHumanCategory}
          />
        )}

        {gameState === 'roleAssignment' && (
          <RoleAssignment
            currentPlayer={currentPlayer}
            roles={roles}
            gameMode={gameMode}
            selectedItem={selectedItem}
            roleRevealed={roleRevealed}
            setRoleRevealed={setRoleRevealed}
            onNextPlayer={nextPlayer}
            numPlayers={numPlayers}
            t={t}
          />
        )}

        {gameState === 'playing' && (
          <GameTimer
            timeLeft={timeLeft}
            formatTime={formatTime}
            gameTimer={gameTimer}
            onShowGameInfo={() => setGameState('finished')}
            onNewGame={newGame}
            t={t}
            onAnotherRound={anotherRound}
          />
        )}

        {gameState === 'finished' && (
          <GameInfo
            selectedItem={selectedItem}
            gameMode={gameMode}
            numPlayers={numPlayers}
            numSpies={numSpies}
            gameTimer={gameTimer}
            timeLeft={timeLeft}
            onBackToTimer={() => setGameState('playing')}
            onNewGame={newGame}
            onAnotherRound={anotherRound}
            t={t}
          />
        )}
        <HowToPlay
          isOpen={showHowToPlay}
          onClose={() => setShowHowToPlay(false)}
          t={t}
        />
      </div>
    </div>
  );
}

export default App;
